import { useTranslation } from "react-i18next";
import { RESTRICTION_AXIOMS, type TermRestriction } from "../../../api/content/ontologyApi";
import { EntityChip, type EntityLookup } from "../viewer/detailParts";
import Chip from "../../../ui/feedback/Chip";

interface TermRestrictionListProps {
  termName: string;
  restrictions: readonly TermRestriction[];
  lookup: EntityLookup;
}

/**
 * Restrictions in plain words (NFR-1): "Every Monk … lives in: at least one Monastery".
 * The property and the class are the terms' own names; a nested class expression is
 * left to the Turtle below.
 */
export default function TermRestrictionList({ termName, restrictions, lookup }: TermRestrictionListProps) {
  const { t } = useTranslation();
  if (restrictions.length === 0) {
    return <p className="app-text-muted text-sm">{t("ontologyViewer.restrictions.none")}</p>;
  }
  return (
    <div className="space-y-3">
      {RESTRICTION_AXIOMS.map((axiom) => {
        const ofAxiom = restrictions.filter((restriction) => restriction.axiom === axiom);
        if (ofAxiom.length === 0) {
          return null;
        }
        return (
          <div key={axiom} className="space-y-1.5">
            <p className="app-text-muted text-xs">{t(`ontologyViewer.restrictions.axioms.${axiom}`, { name: termName })}</p>
            <ul className="space-y-1.5">
              {ofAxiom.map((restriction) => (
                <li
                  key={`${restriction.onProperty} ${restriction.constraint} ${restriction.filler ?? ""} ${String(restriction.cardinality)}`}
                  className="flex flex-wrap items-center gap-1.5 text-sm"
                >
                  <EntityChip iri={restriction.onProperty} lookup={lookup} />
                  <span>{t(`ontologyViewer.restrictions.constraints.${restriction.constraint}`, { count: restriction.cardinality ?? 0 })}</span>
                  <RestrictionFiller restriction={restriction} lookup={lookup} />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function RestrictionFiller({ restriction, lookup }: { restriction: TermRestriction; lookup: EntityLookup }) {
  const { t } = useTranslation();
  if (restriction.filler === null) {
    return (
      <span className="app-text-muted text-xs">
        {restriction.cardinality !== null ? t("ontologyViewer.restrictions.values") : t("ontologyViewer.restrictions.classExpression")}
      </span>
    );
  }
  return restriction.fillerIsIri ? (
    <EntityChip iri={restriction.filler} lookup={lookup} />
  ) : (
    <Chip>“{restriction.filler}”</Chip>
  );
}
